import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { animDuration, delay } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { PetEntity } from 'src/game/logic/EntityManager';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { gsap } from 'gsap';
import { user } from 'src/StateObserver';
import Match2TournamentStreakBar from '../tournament/Match2TournamentStreakBar';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { GemsWallet } from 'src/game/components/GemsWallet';
import { movesPinchCheck } from 'src/lib/pinch';
import {
  getMatch2CloseButtonX,
  match2CloseButtonY,
} from './match2PopupCloseButton';

import { AssetGroup } from '@play-co/timestep-core';

export type Match2PinchType = 'energy' | 'moves';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupMatch2PinchTournamentStreak extends PopupBasic {
  private close: CloseHandler;
  private contentWrapper: View;
  private ctaButton: ButtonScaleViewWithText;
  private gemsWallet: GemsWallet;
  private pet: PetEntity;
  private species: PetSpeciesID | undefined;
  private toggleMessage?: LangBitmapFontTextShadowView;
  private readonly multiplier: Match2TournamentStreakBar;
  private isPopupHidden = false;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const offset = 40;
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      darkerBg: true,
      height: ui.popup.height - offset,
      offsetY: offset - 80,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_POPUP,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();
    this.title.title.updateOpts({ color: '#E25959' });
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.95)',
    });
    this.bg.onClick = () => this.toggleShowPuzzle();

    this.gemsWallet = new GemsWallet({
      superview: this.root,
      tracking: { feature: 'header', $subFeature: 'buy-gems' },
    });

    new ButtonScaleView({
      ...uiConfig.buttons.close,
      superview: this.box,
      x: getMatch2CloseButtonX(this.box.style.width),
      y: match2CloseButtonY,
      onClick: async () => {
        sounds.playSound('cancel');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_BACK,
      },
    });

    this.contentWrapper = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });

    const subpanel = new ImageScaleView({
      superview: this.contentWrapper,
      width: 253,
      height: 170,
      centerOnOrigin: true,
      centerAnchor: true,
      image: 'assets/ui/match2/common/panel_pinch.png',
      x: 395,
      y: 185,
    });

    new LangBitmapFontTextView({
      ...ui.highlightRowLabel,
      superview: subpanel,
      size: 24,
      localeText: () => i18n('match2.movesPinch.message3'),
      align: 'center',
      centerOnOrigin: true,
      x: subpanel.style.width * 0.5,
      y: subpanel.style.height * 0.5 - 3,
      width: subpanel.style.width - 40,
    });

    this.species = Pet.getters.getCurrentEvolution(user())?.species;
    this.pet = new PetEntity('minigame', this.species);
    this.pet.updateOpts({
      superview: this.contentWrapper,
      x: 140,
      y: 285,
      scale: 0.75,
      anchorX: 0.5 * this.pet.style.width,
      anchorY: 0.78 * this.pet.style.height,
    });

    this.multiplier = new Match2TournamentStreakBar({
      superview: this.contentWrapper,
      x: this.contentWrapper.style.width * 0.5,
      y: 330,
      width: 400,
    });

    this.ctaButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.contentWrapper,
      width: 477,
      height: 110,
      centerOnOrigin: true,
      x: this.box.style.width * 0.5,
      y: this.box.style.height - 90,
      fontSize: 50,
      icon: {
        image: 'assets/ui/hud/icons/hud_gem.png',
        width: 54,
        height: 45,
        offset: { x: 190, y: 0 },
      },
      labelOffsetX: -35,
      localeText: () => i18n('match2.movesPinch.cta'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_OK,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/match2/common/panel_pinch.png',
      'assets/ui/hud/icons/hud_gem.png',
    ]);
  }

  public init({ price }: { price: number }) {
    super.init({
      title: i18n(`match2.movesPinch.title3`),
    });

    const petAnimations = petAnimationNames[this.species ?? 'ball'];
    this.pet.setAnimation(petAnimations.cry);

    this.toggleMessage = new LangBitmapFontTextShadowView({
      superview: this.root,
      font: 'Body',
      color: '#FFFFFF',
      width: this.root.style.width,
      height: 100,
      size: 30,
      x: this.root.style.width * 0.5,
      y: 180 + this.box.style.y + this.box.style.height * 0.5,
      localeText: () => i18n('match2.movesPinch.toggleMessage1'),
      centerOnOrigin: true,
      centerAnchor: true,
      verticalAlign: 'center',
      align: 'center',
      scaleY: 0,
      blockEvents: true,
      backgroundColor: 'rgba(0,0,0,0.7)',
    });

    this.ctaButton.label.localeText = () =>
      `${i18n('match2.movesPinch.cta')}  ${price}`;
    this.ctaButton.onClick = async () => {
      sounds.sfx.play('ok');
      const success = await movesPinchCheck(price, 'match2_shop_moves');
      if (success) this.close(true);
    };
  }

  private async toggleShowPuzzle() {
    if (!this.toggleMessage) return;
    this.isPopupHidden = !this.isPopupHidden;

    // Toggle CTAs interactivity accordingly
    this.box.updateOpts({ blockEvents: this.isPopupHidden });

    const duration = 0.5;
    gsap.to(this.bg.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });
    gsap.to(this.box.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });

    await this.tweenToggleMessageScaleY(0);
    this.toggleMessage.localeText = () =>
      i18n(`match2.movesPinch.toggleMessage${this.isPopupHidden ? 2 : 1}`);
    await this.tweenToggleMessageScaleY(1);
  }

  private async tweenToggleMessageScaleY(v: number) {
    if (!this.toggleMessage) return;
    await gsap.to(this.toggleMessage.style, {
      duration: 0.15,
      scaleY: v,
      ease: 'quad.inOut',
      overwrite: true,
    });
  }

  public override async fadeIn() {
    super.fadeIn();
    await delay(animDuration * 2);
    this.multiplier.flash();
    await this.tweenToggleMessageScaleY(1);
  }

  public override async fadeOut() {
    void this.tweenToggleMessageScaleY(0);
    super.fadeOut();
    await delay(animDuration * 2);
    this.multiplier.unflash();
  }
}
